import { useEffect, useLayoutEffect, useRef, useState } from "react";
import "./App.css";
import LoaderOverlay from "./components/LoaderOverlay/LoaderOverlay";
import ToastNotification from "./components/ToastContainer/ToastNotification";
import ProtectedRoutes from "./Routes/ProtectedRoutes";
import { fetchRoles } from "./Redux/Slices/permissionsSlice";
import { useAppDispatch, useAppSelector } from "./Redux/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { useSyncTabs } from "./Hooks/useSyncTabs";
import { useTrackPath } from "./Hooks/useTrackPath";
import NotificationManager from "./components/Notification/NotificationManager";
import OrderNotificationToast from "./components/OrderNotification/OrderNotificationToast";
import { dispatchResetLoader } from "./Redux/Utils/loaderDispatcher";
import { abortInFlightRequests } from "./service/api";
import { toast } from "react-toastify";
import BravePushSettingsModal from "./components/Notification/BravePushSettingsModal";
import { getSellerOrgBuildConfig, resolveAppleTouchIconHref, resolveFaviconHref } from "./config/sellerOrgConfig";
import { useOrganizationBrandingBootstrap } from "./Hooks/useOrganizationBrandingBootstrap";
import { useOrganizationBrandingLoaded, useSellerOrgConfig } from "./Hooks/useSellerOrgConfig";

let fcmServicePromise: Promise<any> | null = null;

const getFcmService = async () => {
  if (!fcmServicePromise) {
    fcmServicePromise = import("./service/FCM/FCMService").then((module) => module.fcmService);
  }
  return fcmServicePromise;
};

const isSecureMessagingContext = () => {
  if (typeof window === "undefined") {
    return false;
  }

  const host = window.location.hostname;
  const isLocalHost = host === "localhost" || host === "127.0.0.1" || host === "[::1]";
  return window.isSecureContext || isLocalHost;
};

const getNotificationPermission = () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return "unsupported";
  }
  return Notification.permission;
};

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = localStorage.getItem("LOGIN_STATUS") === "LOGIN";
  const orgConfig = useSellerOrgConfig();
  const brandingLoaded = useOrganizationBrandingLoaded();
  useOrganizationBrandingBootstrap();
  const roleID =
    useAppSelector((state) => state?.roleAndPermission?.roleID) || 0;
  const location = useLocation();
  const navigate = useNavigate();
  const redirectIfAuthenticatedRoutes = [
    "/",
    "/reset-password",
    "/forgot-password",
    "/registration",
  ];
  const publicRoutes = ["/user-delete", "/terms", "/privacy"];
  const isLoading = useAppSelector((state) => state.loader.loading);
  const fcmToken = useAppSelector((state) => state?.fcm?.token);
  const fcmInitialized = useAppSelector((state) => state?.fcm?.isInitialized);
  const [showBraveModal, setShowBraveModal] = useState(false);
  const scrollUnlockWatchdogIdRef = useRef<number | null>(null);

  useSyncTabs();
  useTrackPath();

  // Ensure global scroll is enabled on authenticated pages.
  // Some auth shells / modal managers may lock scrolling via:
  // - inline styles: `html/body { overflow: hidden; height: 100% }`
  // - body classes: `MuiModal-open` (MUI), `ant-scrolling-effect` (AntD), etc.
  // Use a layout effect so the unlock happens before paint on route transitions.
  useLayoutEffect(() => {
    if (!isAuthenticated) return;
    const unlockScroll = () => {
      const html = document.documentElement;
      const body = document.body;

      // Clear common scroll-lock classes (they can enforce overflow hidden via CSS rules).
      body.classList.remove("MuiModal-open", "ant-scrolling-effect", "ReactModal__Body--open");

      // Clear inline locks.
      html.style.overflow = "";
      body.style.overflow = "";
      html.style.height = "";
      body.style.height = "";

      // Some scroll-lock implementations use fixed positioning.
      body.style.position = "";
      body.style.top = "";
      body.style.left = "";
      body.style.right = "";

      // Modal managers can add padding-right to compensate for scrollbar; clear it.
      body.style.paddingRight = "";
    };

    // Run once immediately (before paint).
    unlockScroll();

    // Watchdog: after login/route change, scroll-lock can be re-applied asynchronously
    // by modal managers. Re-apply unlock repeatedly for a short window.
    if (scrollUnlockWatchdogIdRef.current) {
      window.clearInterval(scrollUnlockWatchdogIdRef.current);
      scrollUnlockWatchdogIdRef.current = null;
    }
    const startedAt = Date.now();
    scrollUnlockWatchdogIdRef.current = window.setInterval(() => {
      unlockScroll();
      if (Date.now() - startedAt > 2000) {
        if (scrollUnlockWatchdogIdRef.current) {
          window.clearInterval(scrollUnlockWatchdogIdRef.current);
          scrollUnlockWatchdogIdRef.current = null;
        }
      }
    }, 50);

    return () => {
      if (scrollUnlockWatchdogIdRef.current) {
        window.clearInterval(scrollUnlockWatchdogIdRef.current);
        scrollUnlockWatchdogIdRef.current = null;
      }
    };
  }, [isAuthenticated, location.pathname]);

  // When order queue becomes empty, navigate to orders page without full page reload
  useEffect(() => {
    const handleOrderQueueEmptyNavigate = (e: CustomEvent<{ path: string }>) => {
      const path = e.detail?.path;
      if (path) {
        navigate(path);
      }
    };
    window.addEventListener('orderQueueEmptyNavigate', handleOrderQueueEmptyNavigate as EventListener);
    return () => window.removeEventListener('orderQueueEmptyNavigate', handleOrderQueueEmptyNavigate as EventListener);
  }, [navigate]);

  // Cancel in-flight API requests from previous page when user navigates
  useEffect(() => {
    abortInFlightRequests();
    dispatchResetLoader();
  }, [location.pathname]);

  // Safety mechanism: Reset loader if it's stuck for more than 60 seconds
  useEffect(() => {
    if (isLoading) {
      const safetyTimeout = setTimeout(() => {
        console.warn('Loader stuck for 60+ seconds, resetting...');
        dispatchResetLoader();
      }, 60000);

      return () => clearTimeout(safetyTimeout);
    }
  }, [isLoading]);

  useEffect(() => {
    if (!brandingLoaded) {
      return;
    }
    const { branding } = orgConfig;
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    const appleIcon = document.querySelector("link[rel='apple-touch-icon']") as HTMLLinkElement;
    if (favicon && appleIcon) {
      const faviconHref = resolveFaviconHref(branding);
      const appleHref = resolveAppleTouchIconHref(branding);
      if (faviconHref) {
        favicon.href = faviconHref;
      }
      if (appleHref) {
        appleIcon.href = appleHref;
      }
      document.title = branding.documentTitle;
    }
  }, [orgConfig, brandingLoaded]);
  useEffect(() => {
    isAuthenticated && roleID === 0 && dispatch(fetchRoles());
    isAuthenticated &&
      redirectIfAuthenticatedRoutes.includes(location.pathname) &&
      !publicRoutes.includes(location.pathname) &&
      navigate("/dashboard", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, location.pathname, navigate]);

  // Initialize FCM when app starts
  // FCM should register for any authenticated user with a valid role
  // RBAC settings control display, not registration
  useEffect(() => {
    const isOrgEligible = orgConfig.features.fcmEnabled;
    // Allow FCM registration for Store Admin (3) and Store User (4)
    // const fcmEligibleRoles = [3, 4];
    // const isRoleEligible = fcmEligibleRoles.includes(roleID);

    // Wait until roleID is loaded (non-zero) before making any FCM decisions
    // if (roleID === 0) {
    //   console.log('[App] Waiting for role to load before FCM initialization...');
    //   return;
    // }

    if (isAuthenticated &&  isOrgEligible) {
      if (!isSecureMessagingContext()) {
        console.warn("[App] Skipping FCM initialization on insecure origin. Use HTTPS or localhost.");
        return;
      }
      console.log('[App] FCM eligible - initializing...', { roleID, orgCode: orgConfig.code });
      getFcmService()
        .then((fcmService) => fcmService.initialize())
        .catch(error => {
          console.error('FCM initialization error:', error);
        });
    } else if (isAuthenticated)  {
      // Only cleanup if role is explicitly not eligible (not just initial 0 state)
      console.log('[App] Role not eligible for FCM, cleaning up...', { roleID });
      getFcmService()
        .then((fcmService) => fcmService.cleanup())
        .catch((error) => {
          console.warn("[App] FCM cleanup skipped:", error);
        });
    }
  }, [isAuthenticated, roleID, orgConfig]);

  // ✅ ENHANCED: Show push notification modal for ALL browsers when push is blocked
  useEffect(() => {
    if (!isAuthenticated || roleID === 0) {
      return;
    }

    const isDismissedRecently = () => {
      const dismissedAtRaw = localStorage.getItem('push_notification_modal_dismissed');
      if (!dismissedAtRaw) return false;
      const dismissedAt = Number(dismissedAtRaw);
      if (Number.isNaN(dismissedAt) || dismissedAt <= 0) return false;
      // Prevent popup fatigue: if user closed modal, don't force-show for 2 hours.
      return Date.now() - dismissedAt < 2 * 60 * 60 * 1000;
    };

    const shouldShowBrowserSettingsPrompt = () => {
      const permissionDenied = getNotificationPermission() === 'denied';
      const browserSoundBlocked = localStorage.getItem('browser_sound_blocked') === 'true';
      const fcmTokenFromStorage = localStorage.getItem('fcm_token');
      const fcmTokenFromWindow = (window as any).__FCM_TOKEN__;
      const hasToken = !!(fcmToken || fcmTokenFromStorage || fcmTokenFromWindow);

      // If user dismissed modal recently, avoid showing repeatedly on every refresh.
      // Exception: when notification permission is explicitly denied.
      if (!permissionDenied && isDismissedRecently()) {
        return false;
      }

      // Always prompt on refresh if browser-level notification/sound blocking is detected
      if (permissionDenied || browserSoundBlocked) {
        return true;
      }

      // Existing fallback: no token after initialization likely means push is blocked
      return !hasToken && fcmInitialized === true;
    };

    // Listen for push-blocked events from firebase.js (works for all browsers)
    const handlePushBlocked = (e: CustomEvent) => {
      console.warn('[App] Push notification blocked event received:', e.detail);

      if (shouldShowBrowserSettingsPrompt()) {
        console.warn('[App] Browser settings appear to block notifications/sound, showing settings modal');
        setShowBraveModal(true);
      }
    };
    const handleBrowserSoundBlocked = (e: Event) => {
      console.warn('[App] Browser sound blocked event received:', (e as CustomEvent)?.detail);
      setShowBraveModal(true);
    };

    window.addEventListener('brave-push-blocked', handlePushBlocked as EventListener);
    window.addEventListener('push-notification-blocked', handlePushBlocked as EventListener);
    window.addEventListener('browser-sound-blocked', handleBrowserSoundBlocked as EventListener);

    let tokenCheckTimeoutId: NodeJS.Timeout | null = null;

    // Wait for FCM to initialize and check if push is actually working
    const checkPushAfterDelay = setTimeout(() => {
      // Check if FCM token is available (use Redux selector values)
      const checkToken = () => {
        // Get current token from Redux (via closure - will use latest values)
        // Also check localStorage as fallback
        const fcmTokenFromStorage = localStorage.getItem('fcm_token');
        const fcmTokenFromWindow = (window as any).__FCM_TOKEN__;
        
        // Use Redux state if available, otherwise fallback to storage/window
        const hasToken = !!(fcmToken || fcmTokenFromStorage || fcmTokenFromWindow);
        
        console.log('[App] Push notification check:', {
          hasToken,
          fcmInitialized,
          notificationPermission: getNotificationPermission(),
          browserSoundBlocked: localStorage.getItem('browser_sound_blocked') === 'true',
          tokenFromRedux: !!fcmToken,
          tokenFromStorage: !!fcmTokenFromStorage,
          tokenFromWindow: !!fcmTokenFromWindow
        });

        if (shouldShowBrowserSettingsPrompt()) {
          console.log('[App] ⚠️ Browser settings check indicates notifications/sound blocked - showing modal');
          setShowBraveModal(true);
        } else if (!hasToken && fcmInitialized === false) {
          console.log('[App] ⏳ FCM still initializing, waiting before checking again...');
        }
      };

      // Check immediately
      checkToken();
      
      // Also check after additional delay to be sure (in case token arrives late)
      tokenCheckTimeoutId = setTimeout(() => {
        checkToken();
      }, 5000); // Wait additional 5 seconds to check for token (increased from 3s)
    }, 12000); // Wait 12 seconds for FCM to initialize (increased from 10s to give more time)

    return () => {
      clearTimeout(checkPushAfterDelay);
      if (tokenCheckTimeoutId) {
        clearTimeout(tokenCheckTimeoutId);
      }
      window.removeEventListener('brave-push-blocked', handlePushBlocked as EventListener);
      window.removeEventListener('push-notification-blocked', handlePushBlocked as EventListener);
      window.removeEventListener('browser-sound-blocked', handleBrowserSoundBlocked as EventListener);
    };
  }, [isAuthenticated, roleID, fcmToken, fcmInitialized]); // Include fcmToken and fcmInitialized to react to changes

  const handleBraveModalRetry = () => {
    // Retry FCM initialization after user enables the setting
    setShowBraveModal(false);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  };

  const handleBraveModalClose = () => {
    setShowBraveModal(false);
  };

  // ✅ Keep modal in sync with real blocked-state checks
  useEffect(() => {
    const permissionDenied = getNotificationPermission() === 'denied';
    const browserSoundBlocked = localStorage.getItem('browser_sound_blocked') === 'true';
    if (showBraveModal && fcmToken && !permissionDenied && !browserSoundBlocked) {
      console.log('[App] 🛡️ Token detected and no browser-level blocks, closing modal');
      setShowBraveModal(false);
    }
  }, [showBraveModal, fcmToken]);

  return (
    <>
      <ProtectedRoutes />
      <LoaderOverlay />
      <ToastNotification />
      <NotificationManager />
      <OrderNotificationToast />
      <BravePushSettingsModal
        open={showBraveModal}
        onClose={handleBraveModalClose}
        onRetry={handleBraveModalRetry}
      />
    </>
  );
}

export default App;